import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Alert,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Badge,
    Tooltip,
    CircularProgress,
    Snackbar,
    Fab,
    Autocomplete,
    ButtonGroup,
    Switch,
    FormControlLabel,
    Avatar,
    CardMedia,
    CardActions,
    Skeleton,
    alpha,
    useTheme
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon,
    ShoppingCart as CartIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Clear as ClearIcon,
    History as HistoryIcon,
    Store as StoreIcon,
    Refresh as RefreshIcon,
    Print as PrintIcon,
    Close as CloseIcon,
    QrCodeScanner as BarcodeIcon,
    Person as PersonIcon,
    LocalOffer as DiscountIcon,
    Pause as HoldIcon,
    ViewModule as GridIcon,
    ViewList as ListIcon,
    CreditCard as CardIcon,
    AccountBalance as CashIcon,
    Email as EmailIcon,
    AttachMoney as MoneyIcon,
    Percent as PercentIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import useSmartSearch from '../../hooks/useSmartSearch';
import SalesHistory from './SalesHistory';

const SalesInterface = ({ branchId }) => {
    const [activeView, setActiveView] = useState('history');
    const theme = useTheme();
    const { user } = useAuth();
    
    // Cart and order management
    const [cart, setCart] = useState([]);
    const [heldOrders, setHeldOrders] = useState([]);
    
    // Error and success states
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Branch management
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    
    // Product filtering and display
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [lowStockOnly, setLowStockOnly] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [productViewMode, setProductViewMode] = useState('grid'); // 'grid' or 'list'
    
    // Get current branch ID first
    const currentBranchId = branchId || selectedBranch?.id;
    
    // Smart search for branch inventory
    const {
        searchTerm,
        setSearchTerm,
        results: products,
        loading,
        isSearchPending,
        refreshCache
    } = useSmartSearch(activeView === 'pos' && currentBranchId ? `/branches/${currentBranchId}/inventory` : null, {
        searchFields: ['name', 'sku', 'barcode'],
        branchId: currentBranchId,
        categoryId: selectedCategory || undefined,
        preloadActive: true,
        minSearchLength: 1
    });
    
    // Customer management
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
    
    // Payment and pricing
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentMethods] = useState([
        { id: 'cash', label: 'Cash', icon: CashIcon },
        { id: 'card', label: 'Card', icon: CardIcon },
        { id: 'digital', label: 'Digital', icon: PaymentIcon }
    ]);
    const [paymentReceived, setPaymentReceived] = useState('');
    const [notes, setNotes] = useState('');
    
    // Discounts and promotions
    const [discount, setDiscount] = useState({ type: 'none', value: 0 }); // 'none', 'percentage', 'fixed'
    const [promoCode, setPromoCode] = useState('');
    const [availableDiscounts, setAvailableDiscounts] = useState([]);
    const [selectedDiscountRule, setSelectedDiscountRule] = useState(null);
    const [discountCalculating, setDiscountCalculating] = useState(false);
    
    // Dialogs
    const [paymentDialog, setPaymentDialog] = useState(false);
    const [receiptDialog, setReceiptDialog] = useState(false);
    const [emailDialog, setEmailDialog] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailLoading, setEmailLoading] = useState(false);
    const [salesHistoryDialog, setSalesHistoryDialog] = useState(false);
    const [barcodeDialog, setBarcodeDialog] = useState(false);
    const [discountDialog, setDiscountDialog] = useState(false);
    const [holdOrderDialog, setHoldOrderDialog] = useState(false);
    const [lastSale, setLastSale] = useState(null);
    
    // Sales history
    const [salesHistory, setSalesHistory] = useState([]);
    
    // Barcode scanning
    const [barcodeInput, setBarcodeInput] = useState('');
    
    // Notifications
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    
    // Loading states
    const [saleLoading, setSaleLoading] = useState(false);
    const [customersLoading, setCustomersLoading] = useState(false);

    // Utility functions first
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };
    const showNotification = useCallback((message, severity = 'info') => {
        setNotification({ open: true, message, severity });
    }, []);

    const closeNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const clearCart = useCallback(() => {
        setCart([]);
        setSelectedCustomer(null);
        setPaymentReceived('');
        setNotes('');
        setDiscount({ type: 'none', value: 0 });
        setPromoCode('');
        setError('');
        setSuccess('');
        setDiscount({ type: 'none', value: 0 });
        setSelectedDiscountRule(null);
        setAvailableDiscounts([]);
        setPromoCode('');
        showNotification('Cart cleared', 'info');
    }, [showNotification]);

    const calculateTotals = useCallback(() => {
        const subtotal = cart.reduce((sum, item) => sum + (item.line_total || 0), 0);
        
        let discountAmount = 0;
        if (discount.type === 'percentage') {
            discountAmount = subtotal * (discount.value / 100);
        } else if (discount.type === 'fixed') {
            discountAmount = Math.min(discount.value, subtotal);
        }
        
        const discountedSubtotal = subtotal - discountAmount;
        const total = discountedSubtotal; // No tax calculation
        
        return { subtotal, discountAmount, total, discountedSubtotal };
    }, [cart, discount]);

    // Calculate available discounts for current cart
    // DISABLED: Coming soon feature
    const calculateAvailableDiscounts = useCallback(async () => {
        // Temporarily disabled - coming soon feature
        setAvailableDiscounts([]);
        setDiscountCalculating(false);
        return;
        
        // if (!currentBranchId || cart.length === 0) {
        //     setAvailableDiscounts([]);
        //     return;
        // }

        // try {
        //     setDiscountCalculating(true);
            
        //     const items = cart.map(item => ({
        //         productId: item.product_id,
        //         quantity: item.quantity,
        //         unitPrice: item.selling_price
        //     }));

        //     const response = await api.post(`/discounts/branches/${currentBranchId}/calculate`, {
        //         items,
        //         customerId: selectedCustomer?.id,
        //         promoCode: promoCode || null
        //     });

        //     setAvailableDiscounts(response.data.applicableDiscounts || []);
        // } catch (error) {
        //     console.error('Error calculating discounts:', error);
        //     setAvailableDiscounts([]);
        // } finally {
        //     setDiscountCalculating(false);
        // }
    }, []);

    // Apply a discount rule
    const applyDiscountRule = useCallback(async (discountRule) => {
        try {
            setSelectedDiscountRule(discountRule);
            
            // Set the discount based on the rule
            if (discountRule.discountType === 'percentage') {
                setDiscount({
                    type: 'percentage',
                    value: discountRule.discountAmount / (cart.reduce((sum, item) => sum + (item.line_total || 0), 0) / 100)
                });
            } else {
                setDiscount({
                    type: 'fixed',
                    value: discountRule.discountAmount
                });
            }

            showNotification(`Applied discount: ${discountRule.name}`, 'success');
            setDiscountDialog(false);
        } catch (error) {
            console.error('Error applying discount rule:', error);
            showNotification('Failed to apply discount', 'error');
        }
    }, [cart, showNotification]);

    const handlePayment = useCallback(() => {
        if (cart.length === 0) {
            showNotification('Cart is empty!', 'error');
            return;
        }
        
        const { total } = calculateTotals();
        setPaymentReceived(total.toFixed(2));
        setPaymentDialog(true);
    }, [cart.length, calculateTotals, showNotification]);

    // Fetch functions
    const fetchBranches = useCallback(async () => {
        try {
            const response = await api.get('/branches');
            setBranches(response.data.branches || []);
            
            if (response.data.branches && response.data.branches.length > 0) {
                if (user.role === 'admin') {
                    setSelectedBranch(response.data.branches[0]);
                } else if (user.assignedBranches && user.assignedBranches.length > 0) {
                    const assignedBranch = response.data.branches.find(branch => 
                        user.assignedBranches.some(ab => ab.id === branch.id)
                    );
                    setSelectedBranch(assignedBranch || response.data.branches[0]);
                } else {
                    setSelectedBranch(response.data.branches[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching branches:', error);
            showNotification('Failed to load branches', 'error');
        }
    }, [user.role, user.assignedBranches, showNotification]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await api.get('/products/categories');
            setCategories(response.data.categories || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        if (!currentBranchId) return;
        
        try {
            refreshCache(); // This will now fetch from /branches/{branchId}/inventory
            setError('');
        } catch (error) {
            console.error('Error fetching products:', error);
            setError('Failed to load products. Please try again.');
            showNotification('Failed to load products', 'error');
        }
    }, [currentBranchId, refreshCache, showNotification]);

    const fetchCustomers = useCallback(async () => {
        try {
            setCustomersLoading(true);
            const response = await api.get('/customers');
            setCustomers(response.data.customers || []);
        } catch (error) {
            console.error('Error fetching customers:', error);
            // Don't show error for customers as it might not be implemented yet
            setCustomers([]);
        } finally {
            setCustomersLoading(false);
        }
    }, []);


    const fetchHeldOrders = useCallback(async () => {
        if (!currentBranchId) return;
        
        try {
            const response = await api.get(`/branches/${currentBranchId}/held-orders`);
            setHeldOrders(response.data.orders || []);
        } catch (error) {
            console.error('Error fetching held orders:', error);
            // Set empty array if endpoint doesn't exist
            setHeldOrders([]);
        }
    }, [currentBranchId]);

    const holdCurrentOrder = useCallback(async () => {
        if (cart.length === 0) {
            showNotification('Cart is empty!', 'error');
            return;
        }

        try {
            const orderData = {
                customerId: selectedCustomer?.id || null,
                items: cart,
                notes: notes || 'Held order',
                timestamp: new Date().toISOString()
            };

            await api.post(`/branches/${currentBranchId}/held-orders`, orderData);
            showNotification('Order held successfully', 'success');
            clearCart();
            fetchHeldOrders();
        } catch (error) {
            console.error('Error holding order:', error);
            if (error.response?.status === 404) {
                showNotification('Hold order feature not available', 'warning');
            } else {
                showNotification('Failed to hold order', 'error');
            }
        }
    }, [cart, selectedCustomer, notes, currentBranchId, showNotification, clearCart, fetchHeldOrders]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (event) => {
            // Prevent shortcuts when typing in input fields
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;
            
            switch (event.key.toLowerCase()) {
                case 'f1':
                    event.preventDefault();
                    handlePayment();
                    break;
                case 'f2':
                    event.preventDefault();
                    setBarcodeDialog(true);
                    break;
                case 'f3':
                    event.preventDefault();
                    setCustomerDialogOpen(true);
                    break;
                case 'f4':
                    event.preventDefault();
                    holdCurrentOrder();
                    break;
                case 'escape':
                    clearCart();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [cart, clearCart, handlePayment, holdCurrentOrder]);

    useEffect(() => {
        fetchBranches();
        fetchCategories();
        fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentBranchId && activeView === 'pos') {
            fetchProducts();
            fetchHeldOrders();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBranchId, activeView]);

    useEffect(() => {
        // Filter products based on search term, category, and stock level
        let filtered = products;
        
        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        
        if (selectedCategory) {
            filtered = filtered.filter(product => product.category_name === selectedCategory);
        }
        
        if (lowStockOnly) {
            filtered = filtered.filter(product => product.quantity <= (product.reorder_level || 5));
        }
        
        setFilteredProducts(filtered);
    }, [searchTerm, products, selectedCategory, lowStockOnly]);

    const fetchSalesHistory = async () => {
        if (!currentBranchId) return;
        
        try {
            const response = await api.get(`/branches/${currentBranchId}/sales`);
            setSalesHistory(response.data.sales || []);
        } catch (error) {
            console.error('Error fetching sales history:', error);
            showNotification('Failed to load sales history', 'error');
        }
    };

    const addToCart = (product) => {
        const availableQuantity = Number(product.quantity || 0);
        if (availableQuantity <= 0) {
            showNotification(`${product.name} is out of stock!`, 'error');
            return;
        }

        const existingItem = cart.find(item => item.product_id === product.product_id);
        
        if (existingItem) {
            if (existingItem.quantity >= availableQuantity) {
                showNotification(`Cannot add more ${product.name}. Only ${availableQuantity} available.`, 'warning');
                return;
            }
            updateCartQuantity(product.product_id, existingItem.quantity + 1);
        } else {
            const cartItem = {
                product_id: product.product_id,
                name: product.name || 'Unknown Product',
                sku: product.sku || 'N/A',
                barcode: product.barcode || '',
                selling_price: Number(product.selling_price || 0),
                available_quantity: availableQuantity,
                quantity: 1,
                line_total: Number(product.selling_price || 0),
                image: product.image || null
            };
            setCart([...cart, cartItem]);
            showNotification(`${product.name} added to cart`, 'success');
        }
        setError('');
    };

    const updateCartQuantity = (productId, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(productId);
            return;
        }

        setCart(cart.map(item => {
            if (item.product_id === productId) {
                if (newQuantity > item.available_quantity) {
                    showNotification(`Cannot add more ${item.name}. Only ${item.available_quantity} available.`, 'warning');
                    return item;
                }
                return {
                    ...item,
                    quantity: newQuantity,
                    line_total: newQuantity * (item.selling_price || 0)
                };
            }
            return item;
        }));
        setError('');
    };

    const removeFromCart = (productId) => {
        const item = cart.find(item => item.product_id === productId);
        setCart(cart.filter(item => item.product_id !== productId));
        if (item) {
            showNotification(`${item.name} removed from cart`, 'info');
        }
    };

    const processSale = async () => {
        try {
            setSaleLoading(true);
            
            const { subtotal, discountAmount, total } = calculateTotals();
            const received = parseFloat(paymentReceived) || 0;
            
            if (received < total) {
                setError('Payment received is less than total amount!');
                return;
            }

            const saleData = {
                customerId: selectedCustomer?.id || null,
                customerName: selectedCustomer?.name || null,
                customerPhone: selectedCustomer?.phone || null,
                items: cart.map(item => ({
                    productId: item.product_id,
                    quantity: item.quantity,
                    unitPrice: item.selling_price
                })),
                paymentMethod,
                paymentReceived: received,
                subtotal,
                discountAmount,
                totalAmount: total,
                notes: notes || null,
                promoCode: promoCode || null
            };

            const response = await api.post(`/branches/${currentBranchId}/sales`, saleData);
            
            setLastSale(response.data.sale);
            setSuccess(`Sale completed successfully! Sale #${response.data.sale.saleNumber}`);
            setPaymentDialog(false);
            setReceiptDialog(true);
            
            // Refresh data
            await fetchProducts();
            
            clearCart();
            
        } catch (error) {
            console.error('Error processing sale:', error);
            setError(error.response?.data?.message || 'Failed to process sale. Please try again.');
        } finally {
            setSaleLoading(false);
        }
    };

    const recallHeldOrder = async (orderId) => {
        try {
            const response = await api.get(`/branches/${currentBranchId}/held-orders/${orderId}`);
            const order = response.data.order;
            
            setCart(order.items);
            if (order.customerId) {
                const customer = customers.find(c => c.id === order.customerId);
                setSelectedCustomer(customer);
            }
            setNotes(order.notes || '');
            
            // Remove from held orders
            await api.delete(`/branches/${currentBranchId}/held-orders/${orderId}`);
            fetchHeldOrders();
            
            showNotification('Order recalled successfully', 'success');
            setHoldOrderDialog(false);
        } catch (error) {
            console.error('Error recalling order:', error);
            showNotification('Failed to recall order', 'error');
        }
    };

    const handleBarcodeSearch = () => {
        if (!barcodeInput.trim()) return;
        
        const product = products.find(p => 
            p.barcode && p.barcode.toLowerCase() === barcodeInput.toLowerCase()
        );
        
        if (product) {
            addToCart(product);
            setBarcodeInput('');
            setBarcodeDialog(false);
        } else {
            showNotification('Product not found with this barcode', 'error');
        }
    };

    const applyDiscount = () => {
        if (discount.value <= 0) {
            showNotification('Please enter a valid discount amount', 'error');
            return;
        }
        
        showNotification(`Discount applied: ${discount.type === 'percentage' ? discount.value + '%' : 'KES ' + discount.value}`, 'success');
        setDiscountDialog(false);
    };

    // Calculate available discounts when cart changes
    // DISABLED: Coming soon feature
    // useEffect(() => {
    //     if (cart.length > 0) {
    //         calculateAvailableDiscounts();
    //     } else {
    //         setAvailableDiscounts([]);
    //     }
    // }, [cart, selectedCustomer, calculateAvailableDiscounts]);

    const createCustomer = async () => {
        try {
            const response = await api.post('/customers', newCustomer);
            const customer = response.data.customer;
            setCustomers([...customers, customer]);
            setSelectedCustomer(customer);
            setNewCustomer({ name: '', phone: '', email: '' });
            setCustomerDialogOpen(false);
            showNotification('Customer created successfully', 'success');
        } catch (error) {
            console.error('Error creating customer:', error);
            if (error.response?.status === 404) {
                showNotification('Customer management not available', 'warning');
            } else {
                showNotification('Failed to create customer', 'error');
            }
        }
    };

    const sendEmailReceipt = async () => {
        if (!emailAddress.trim()) {
            showNotification('Please enter an email address', 'error');
            return;
        }

        if (!lastSale?.id) {
            showNotification('No sale data available for email', 'error');
            return;
        }

        try {
            setEmailLoading(true);
            const response = await api.post(`/branches/${currentBranchId}/sales/email-receipt/${lastSale.id}`, {
                email: emailAddress.trim()
            });

            if (response.data.emailSent) {
                showNotification(`Receipt emailed successfully to ${emailAddress}`, 'success');
                setEmailDialog(false);
                setEmailAddress('');
            } else {
                showNotification('Failed to send email receipt', 'error');
            }
        } catch (error) {
            console.error('Error sending email receipt:', error);
            showNotification(
                error.response?.data?.message || 'Failed to send email receipt',
                'error'
            );
        } finally {
            setEmailLoading(false);
        }
    };

    const { subtotal, discountAmount, total } = calculateTotals();
    const changeAmount = paymentReceived ? parseFloat(paymentReceived) - total : 0;

    const ProductCard = ({ product }) => {
        const qty = Number(product.quantity || 0);
        const isOutOfStock = qty <= 0;
        const isLowStock = qty > 0 && qty <= (product.reorder_level || 5);
        const stockColor = isOutOfStock
            ? theme.palette.error.main
            : isLowStock
                ? theme.palette.warning.main
                : theme.palette.success.main;

        return (
            <Card
                elevation={0}
                sx={{
                    height: '100%',
                    minWidth: 160,
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                    }
                }}
                onClick={() => addToCart(product)}
            >
                <CardMedia
                    component="div"
                    sx={{
                        height: 100,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    {product.image ? (
                        <img
                            src={product.image}
                            alt={product.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <Avatar
                            sx={{
                                width: 48,
                                height: 48,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                            }}
                        >
                            <StoreIcon sx={{ fontSize: 24 }} />
                        </Avatar>
                    )}
                    <Chip
                        label={qty}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 24,
                            bgcolor: alpha(stockColor, 0.12),
                            color: stockColor,
                            border: '1px solid',
                            borderColor: alpha(stockColor, 0.3),
                        }}
                    />
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 1.5, pb: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.3,
                            minHeight: '2.6em',
                            color: 'text.primary'
                        }}
                    >
                        {product.name || 'Unknown Product'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block" mt={0.5}>
                        {product.sku || 'N/A'}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            mt: 0.5,
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            fontSize: '1rem'
                        }}
                    >
                        KES {formatCurrency(Number(product.selling_price || 0))}
                    </Typography>
                </CardContent>
                <CardActions sx={{ p: 1.5, pt: 0 }}>
                    <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disabled={isOutOfStock}
                        onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product);
                        }}
                        sx={{
                            py: 0.75,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            }
                        }}
                    >
                        {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                </CardActions>
            </Card>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: 'calc(100vh - 128px)' }}>
            {/* Page Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Sales
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {activeView === 'history' ? 'View and manage all transactions' : selectedBranch?.name ? `Branch: ${selectedBranch.name}` : 'Point of sale interface'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {activeView === 'pos' && branches.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Branch</InputLabel>
                            <Select
                                value={selectedBranch?.id || ''}
                                label="Branch"
                                onChange={(e) => {
                                    const branch = branches.find(b => b.id === e.target.value);
                                    setSelectedBranch(branch);
                                }}
                                sx={{ borderRadius: 2 }}
                            >
                                {branches.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {activeView === 'pos' && (
                        <Tooltip title="Refresh Products">
                            <span>
                                <IconButton
                                    onClick={fetchProducts}
                                    disabled={loading || isSearchPending}
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                                    }}
                                >
                                    <RefreshIcon sx={{ color: 'primary.main' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    {activeView === 'history' && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setActiveView('pos')}
                        >
                            New Sale
                        </Button>
                    )}
                    {activeView === 'pos' && (
                        <Button
                            variant="outlined"
                            startIcon={<HistoryIcon />}
                            onClick={() => setActiveView('history')}
                        >
                            Sales History
                        </Button>
                    )}
                </Box>
            </Box>

            {activeView === 'history' && <SalesHistory />}

            {activeView === 'pos' && (<>
            {/* Tabs divider — POS content below */}

            {/* Main Content */}
            <Box sx={{ display: 'flex', gap: 2, minHeight: 'calc(100vh - 260px)' }}>
                {/* Left Panel - Products */}
                <Paper elevation={0} sx={{
                    flex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minHeight: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3
                }}>
                    {/* Search and Filters */}
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                                placeholder="Search products by name, SKU, or barcode..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                size="small"
                                sx={{
                                    flex: 1,
                                    minWidth: 200,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: 'background.default'
                                    }
                                }}
                            />
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={selectedCategory}
                                    label="Category"
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    sx={{ borderRadius: 3 }}
                                >
                                    <MenuItem value="">All Categories</MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.name}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={lowStockOnly}
                                        onChange={(e) => setLowStockOnly(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Low Stock"
                                sx={{ mr: 0 }}
                            />
                            <ButtonGroup size="small">
                                <Button
                                    variant={productViewMode === 'grid' ? 'contained' : 'outlined'}
                                    onClick={() => setProductViewMode('grid')}
                                >
                                    <GridIcon />
                                </Button>
                                <Button
                                    variant={productViewMode === 'list' ? 'contained' : 'outlined'}
                                    onClick={() => setProductViewMode('list')}
                                >
                                    <ListIcon />
                                </Button>
                            </ButtonGroup>
                        </Box>
                    </Box>

                    {/* Products Display */}
                    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                        {loading ? (
                            <Grid container spacing={2}>
                                {[1, 2, 3, 4, 5, 6].map((item) => (
                                    <Grid size={{ xs: 6, sm: 6, md: 4, lg: 3 }} key={item}>
                                        <Card>
                                            <Skeleton variant="rectangular" height={120} />
                                            <CardContent>
                                                <Skeleton />
                                                <Skeleton width="60%" />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : productViewMode === 'grid' ? (
                            <Grid container spacing={2}>
                                {filteredProducts.map((product) => (
                                    <Grid size={{ xs: 6, sm: 6, md: 4, lg: 3 }} key={product.product_id || product.id}>
                                        <ProductCard product={product} />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <TableContainer>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell align="right">Stock</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredProducts.map((product) => (
                                            <TableRow 
                                                key={product.product_id || product.id}
                                                sx={{ '&:hover': { bgcolor: 'background.default' } }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar sx={{
                                                            width: 32,
                                                            height: 32,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            color: theme.palette.primary.main,
                                                        }}>
                                                            <StoreIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {product.name || 'Unknown Product'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{product.sku || 'N/A'}</TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="h6" color="primary">
                                                        KES {formatCurrency(Number(product.selling_price || 0))}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={Number(product.quantity || 0)}
                                                        color={Number(product.quantity || 0) > 0 ? 'success' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => addToCart(product)}
                                                        disabled={Number(product.quantity || 0) <= 0}
                                                    >
                                                        Add
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        
                        {filteredProducts.length === 0 && !loading && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Avatar sx={{
                                    width: 72,
                                    height: 72,
                                    bgcolor: alpha(theme.palette.info.main, 0.08),
                                    color: theme.palette.info.main,
                                    mx: 'auto',
                                    mb: 2
                                }}>
                                    <StoreIcon sx={{ fontSize: 36 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                    No products found
                                </Typography>
                                <Typography variant="body2" color="text.disabled">
                                    Try adjusting your search or filters
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Right Panel - Cart & Checkout */}
                <Paper elevation={0} sx={{
                    width: 420,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    height: '100%',
                    maxHeight: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3
                }}>
                    {/* Cart Header */}
                    <Box sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: 'primary.main',
                                }}>
                                    <CartIcon sx={{ fontSize: 20 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, color: 'text.primary' }}>
                                        Shopping Cart
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {cart.length} {cart.length === 1 ? 'item' : 'items'}
                                    </Typography>
                                </Box>
                            </Box>
                            {cart.length > 0 && (
                                <Tooltip title="Clear Cart (ESC)">
                                    <IconButton onClick={clearCart} size="small" sx={{
                                        color: 'text.secondary',
                                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                                        '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main' }
                                    }}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {/* Customer Selection */}
                    <Box sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Avatar sx={{
                                width: 24,
                                height: 24,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                            }}>
                                <PersonIcon sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Customer
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Autocomplete
                                size="small"
                                fullWidth
                                options={customers}
                                getOptionLabel={(option) => `${option.name} - ${option.phone}`}
                                value={selectedCustomer}
                                onChange={(event, newValue) => setSelectedCustomer(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search or select customer"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: 'background.paper'
                                            }
                                        }}
                                    />
                                )}
                                loading={customersLoading}
                            />
                            <Tooltip title="New Customer (F3)">
                                <IconButton
                                    onClick={() => setCustomerDialogOpen(true)}
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main,
                                        flexShrink: 0,
                                        width: 38,
                                        height: 38,
                                        borderRadius: 2,
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                                        }
                                    }}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Cart Items - Scrollable Section */}
                    <Box sx={{ 
                        flex: '1 1 auto',
                        overflow: 'auto',
                        minHeight: 0,
                        maxHeight: 'calc(100% - 300px)' // Reserve space for header + actions + totals
                    }}>
                        {cart.length === 0 ? (
                            <Box sx={{
                                textAlign: 'center',
                                py: 4,
                                px: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                minHeight: 150
                            }}>
                                <Avatar sx={{
                                    width: 64,
                                    height: 64,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    mb: 2
                                }}>
                                    <CartIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                    Cart is empty
                                </Typography>
                                <Typography variant="body2" color="text.disabled">
                                    Click on products to start a sale
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0, height: '100%' }}>
                                {cart.map((item, index) => (
                                    <ListItem 
                                        key={item.product_id} 
                                        divider={index < cart.length - 1}
                                        sx={{ px: 2, py: 1.5 }}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            width: '100%', 
                                            gap: 1.5 
                                        }}>
                                            <Avatar sx={{
                                                width: 36,
                                                height: 36,
                                                flexShrink: 0,
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                color: theme.palette.primary.main,
                                            }}>
                                                <StoreIcon sx={{ fontSize: 18 }} />
                                            </Avatar>
                                            
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight="bold" noWrap>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    KES {formatCurrency(Number(item.selling_price || 0))} each
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 0.5,
                                                flexShrink: 0 
                                            }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => updateCartQuantity(item.product_id, item.quantity - 1)}
                                                    sx={{ bgcolor: 'action.hover', width: 28, height: 28 }}
                                                >
                                                    <RemoveIcon fontSize="small" />
                                                </IconButton>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight="bold"
                                                    sx={{ minWidth: 20, textAlign: 'center' }}
                                                >
                                                    {item.quantity}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)}
                                                    sx={{ bgcolor: 'action.hover', width: 28, height: 28 }}
                                                >
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            
                                            <Box sx={{ textAlign: 'right', minWidth: 70, flexShrink: 0 }}>
                                                <Typography variant="body2" fontWeight="bold" noWrap>
                                                    KES {formatCurrency(Number(item.line_total || 0))}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => removeFromCart(item.product_id)}
                                                    sx={{ width: 24, height: 24, mt: 0.5 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>

                    {/* Cart Actions & Totals - Fixed at bottom */}
                    {cart.length > 0 && (
                        <Box sx={{ 
                            borderTop: 1, 
                            borderColor: 'divider',
                            flexShrink: 0,
                            bgcolor: 'white',
                            mt: 'auto'
                        }}>
                            {/* Quick Actions */}
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <Grid container spacing={1}>
                                    <Grid size={4}>
                                        <Tooltip title="Coming Soon">
                                            <span>
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<DiscountIcon />}
                                                    disabled
                                                    sx={{ fontSize: '0.75rem' }}
                                                >
                                                    Discount
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                    <Grid size={4}>
                                        <Tooltip title="Coming Soon">
                                            <span>
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<HoldIcon />}
                                                    disabled
                                                    sx={{ fontSize: '0.75rem' }}
                                                >
                                                    Hold
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                    <Grid size={4}>
                                        <Tooltip title="Coming Soon">
                                            <span>
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<HistoryIcon />}
                                                    disabled
                                                    sx={{ fontSize: '0.75rem' }}
                                                >
                                                    Recall
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Totals */}
                            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">Subtotal:</Typography>
                                    <Typography variant="body2" fontWeight="bold">KES {formatCurrency(subtotal)}</Typography>
                                </Box>
                                
                                {discountAmount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="success.main" noWrap>
                                            Discount ({discount.type === 'percentage' ? `${discount.value}%` : `KES ${discount.value}`}):
                                        </Typography>
                                        <Typography variant="body2" color="success.main" fontWeight="bold">
                                            -KES {formatCurrency(discountAmount)}
                                        </Typography>
                                    </Box>
                                )}
                                
                                <Divider sx={{ my: 1 }} />
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="h6" fontWeight="bold">Total:</Typography>
                                    <Typography variant="h6" fontWeight="bold" color="primary">
                                        KES {formatCurrency(total)}
                                    </Typography>
                                </Box>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    startIcon={<PaymentIcon />}
                                    onClick={handlePayment}
                                    disabled={saleLoading}
                                    sx={{
                                        py: 1.2,
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                    }}
                                >
                                    Process Payment (F1)
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Error/Success Messages */}
            {error && (
                <Alert severity="error" sx={{ m: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            
            {success && (
                <Alert severity="success" sx={{ m: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Floating Action Buttons */}
            <Box sx={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {heldOrders.length > 0 && (
                    <Fab 
                        color="secondary" 
                        size="small"
                        onClick={() => setHoldOrderDialog(true)}
                    >
                        <Badge badgeContent={heldOrders.length} color="error">
                            <HoldIcon />
                        </Badge>
                    </Fab>
                )}
                <Fab 
                    color="info" 
                    size="small"
                    onClick={() => {
                        fetchSalesHistory();
                        setSalesHistoryDialog(true);
                    }}
                >
                    <HistoryIcon />
                </Fab>
            </Box>

            {/* Dialogs */}
            
            {/* Payment Dialog */}
            <Dialog
                open={paymentDialog}
                onClose={() => !saleLoading && setPaymentDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <PaymentIcon sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Process Payment
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Complete the transaction
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {/* Total Amount */}
                    <Box sx={{
                        mb: 3,
                        p: 2.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Total Amount
                        </Typography>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                            KES {formatCurrency(total)}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* Payment Method */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment Method</Typography>
                            <ButtonGroup fullWidth variant="outlined">
                                {paymentMethods.map((method) => (
                                    <Button
                                        key={method.id}
                                        variant={paymentMethod === method.id ? 'contained' : 'outlined'}
                                        onClick={() => setPaymentMethod(method.id)}
                                        startIcon={<method.icon />}
                                    >
                                        {method.label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Box>

                        {/* Payment Received */}
                        <TextField
                            fullWidth
                            type="number"
                            label="Payment Received"
                            value={paymentReceived}
                            onChange={(e) => setPaymentReceived(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-input': {
                                    fontSize: '1.5rem',
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }
                            }}
                        />

                        {/* Change Amount */}
                        {changeAmount >= 0 && paymentReceived && (
                            <Box sx={{
                                bgcolor: 'success.main',
                                color: 'white',
                                p: 2,
                                borderRadius: 2,
                                textAlign: 'center'
                            }}>
                                <Typography variant="h6" fontWeight="bold">
                                    Change: KES {formatCurrency(changeAmount)}
                                </Typography>
                            </Box>
                        )}

                        {/* Notes */}
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Notes (Optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setPaymentDialog(false)}
                        disabled={saleLoading}
                        sx={{ mr: 1 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={processSale}
                        disabled={saleLoading || changeAmount < 0}
                        startIcon={saleLoading ? <CircularProgress size={20} /> : <PaymentIcon />}
                    >
                        {saleLoading ? 'Processing...' : 'Complete Sale'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Barcode Scanner Dialog */}
            <Dialog open={barcodeDialog} onClose={() => setBarcodeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BarcodeIcon />
                        Barcode Scanner
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Scan or enter barcode"
                        value={barcodeInput}
                        onChange={(e) => setBarcodeInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleBarcodeSearch()}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBarcodeDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleBarcodeSearch}>
                        Search
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Customer Dialog */}
            <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Customer</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Customer Name"
                                value={newCustomer.name}
                                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                value={newCustomer.phone}
                                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Email (Optional)"
                                value={newCustomer.email}
                                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={createCustomer}>
                        Create Customer
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Enhanced Discount Dialog */}
            <Dialog open={discountDialog} onClose={() => setDiscountDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DiscountIcon />
                        Apply Discount
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        {/* Available Discount Rules */}
                        {availableDiscounts.length > 0 && (
                            <Grid size={12}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                    Available Discounts
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 200, overflow: 'auto' }}>
                                    {availableDiscounts.map((rule) => (
                                        <Card key={rule.id} sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'background.default' } }}
                                              onClick={() => applyDiscountRule(rule)}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {rule.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {rule.description || 'Click to apply this discount'}
                                                    </Typography>
                                                    {rule.requiresApproval && (
                                                        <Chip label="Requires Approval" size="small" color="warning" sx={{ mt: 0.5 }} />
                                                    )}
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="h6" color="success.main" fontWeight="bold">
                                                        -KES {formatCurrency(rule.discountAmount)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {rule.discountType === 'percentage' ? 'Percentage' : 
                                                         rule.discountType === 'fixed_amount' ? 'Fixed Amount' : 'Special Offer'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Card>
                                    ))}
                                </Box>
                            </Grid>
                        )}

                        {/* Manual Discount Entry */}
                        <Grid size={12}>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ mt: availableDiscounts.length > 0 ? 2 : 0 }}>
                                Manual Discount
                            </Typography>
                            <ButtonGroup fullWidth variant="outlined" sx={{ mb: 2 }}>
                                <Button
                                    variant={discount.type === 'percentage' ? 'contained' : 'outlined'}
                                    onClick={() => setDiscount({ ...discount, type: 'percentage' })}
                                    startIcon={<PercentIcon />}
                                >
                                    Percentage
                                </Button>
                                <Button
                                    variant={discount.type === 'fixed' ? 'contained' : 'outlined'}
                                    onClick={() => setDiscount({ ...discount, type: 'fixed' })}
                                    startIcon={<MoneyIcon />}
                                >
                                    Fixed Amount
                                </Button>
                            </ButtonGroup>
                        </Grid>
                        
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label={discount.type === 'percentage' ? 'Discount Percentage' : 'Discount Amount (KES)'}
                                value={discount.value}
                                onChange={(e) => setDiscount({ ...discount, value: parseFloat(e.target.value) || 0 })}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {discount.type === 'percentage' ? '%' : 'KES'}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Promo Code (Optional)"
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                onBlur={calculateAvailableDiscounts}
                            />
                        </Grid>

                        {/* Discount Preview */}
                        {(discount.value > 0 || selectedDiscountRule) && (
                            <Grid size={12}>
                                <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Discount Preview
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>Current Subtotal:</Typography>
                                        <Typography>KES {formatCurrency(cart.reduce((sum, item) => sum + (item.line_total || 0), 0))}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>Discount:</Typography>
                                        <Typography>-KES {formatCurrency(calculateTotals().discountAmount)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', mt: 1 }}>
                                        <Typography>New Subtotal:</Typography>
                                        <Typography>KES {formatCurrency(calculateTotals().discountedSubtotal)}</Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        )}

                        {discountCalculating && (
                            <Grid size={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2">Calculating available discounts...</Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiscountDialog(false)}>Cancel</Button>
                    <Button 
                        variant="outlined" 
                        onClick={() => {
                            setDiscount({ type: 'none', value: 0 });
                            setSelectedDiscountRule(null);
                            showNotification('Discount removed', 'info');
                        }}
                        disabled={discount.type === 'none' && !selectedDiscountRule}
                    >
                        Remove Discount
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={applyDiscount}
                        disabled={discount.value <= 0}
                    >
                        Apply Manual Discount
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Hold Orders Dialog */}
            <Dialog open={holdOrderDialog} onClose={() => setHoldOrderDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Held Orders</DialogTitle>
                <DialogContent>
                    {heldOrders.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <HoldIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                            <Typography>No held orders</Typography>
                        </Box>
                    ) : (
                        <List>
                            {heldOrders.map((order) => (
                                <ListItem key={order.id} divider>
                                    <ListItemText
                                        primary={`Order #${order.id} - ${order.items.length} items`}
                                        secondary={`${order.customer?.name || 'Walk-in'} - ${new Date(order.timestamp).toLocaleString()}`}
                                    />
                                    <ListItemSecondaryAction>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => recallHeldOrder(order.id)}
                                        >
                                            Recall
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHoldOrderDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Receipt Dialog */}
            <Dialog open={receiptDialog} onClose={() => setReceiptDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ReceiptIcon sx={{ mr: 1 }} />
                        Sale Receipt
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {lastSale && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Sale #{lastSale.saleNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {new Date(lastSale.createdAt).toLocaleString()}
                            </Typography>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            {lastSale.items.map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography>{item.productName} × {item.quantity}</Typography>
                                    <Typography>KES {formatCurrency(item.lineTotal)}</Typography>
                                </Box>
                            ))}
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Subtotal:</Typography>
                                <Typography>KES {formatCurrency(lastSale.subtotal)}</Typography>
                            </Box>
                            {lastSale.discountAmount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography color="success.main">Discount:</Typography>
                                    <Typography color="success.main">-KES {formatCurrency(lastSale.discountAmount)}</Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                <Typography variant="h6">Total:</Typography>
                                <Typography variant="h6">KES {formatCurrency(lastSale.totalAmount)}</Typography>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Payment Method:</Typography>
                                <Typography sx={{ textTransform: 'capitalize' }}>
                                    {lastSale.paymentMethod}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Payment Received:</Typography>
                                <Typography>KES {formatCurrency(lastSale.paymentReceived)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography>Change:</Typography>
                                <Typography>KES {formatCurrency(lastSale.changeAmount)}</Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReceiptDialog(false)}>Close</Button>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
                        Print
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<EmailIcon />} 
                        onClick={() => setEmailDialog(true)}
                        disabled={!lastSale}
                    >
                        Email
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email Receipt Dialog */}
            <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon />
                        Email Receipt
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Send receipt for Sale #{lastSale?.saleNumber} to customer email
                        </Typography>
                        <TextField
                            fullWidth
                            type="email"
                            label="Customer Email Address"
                            value={emailAddress}
                            onChange={(e) => setEmailAddress(e.target.value)}
                            placeholder="customer@example.com"
                            sx={{ mt: 2 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">
                                    <EmailIcon />
                                </InputAdornment>,
                            }}
                            onKeyPress={(e) => e.key === 'Enter' && sendEmailReceipt()}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialog(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={sendEmailReceipt}
                        disabled={emailLoading || !emailAddress.trim()}
                        startIcon={emailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
                    >
                        {emailLoading ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Sales History Dialog */}
            <Dialog open={salesHistoryDialog} onClose={() => setSalesHistoryDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <HistoryIcon sx={{ mr: 1 }} />
                            Sales History
                        </Box>
                        <IconButton onClick={() => setSalesHistoryDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sale #</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell align="right">Items</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell>Payment</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {salesHistory.map((sale) => (
                                    <TableRow key={sale.id}>
                                        <TableCell>{sale.sale_number}</TableCell>
                                        <TableCell>{new Date(sale.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell>{sale.customer_name || 'Walk-in'}</TableCell>
                                        <TableCell align="right">{sale.items?.length || 0}</TableCell>
                                        <TableCell align="right">KES {formatCurrency(sale.total_amount)}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={sale.payment_method} 
                                                size="small" 
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={sale.status} 
                                                color={sale.status === 'completed' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Receipt">
                                                <IconButton size="small">
                                                    <ReceiptIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>

            </>)}

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={closeNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SalesInterface;